import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SpinLoading } from 'antd-mobile';
import { useUserStore } from '@/store/useUserStore';

interface RouteGuardProps {
  children: React.ReactNode;
  requireAuth?: boolean; // 是否需要登录
  redirectTo?: string; // 未登录时重定向的路径
}

/**
 * 路由守卫组件
 * 用于检查用户登录状态和权限控制
 *
 * @param requireAuth 是否需要登录，默认为 true
 * @param redirectTo 未登录时重定向的路径，默认为 '/login'
 * @param children 子组件
 */
const RouteGuard: React.FC<RouteGuardProps> = ({
  children,
  requireAuth = true,
  redirectTo = '/login',
}) => {
  const { token, getUser, logout } = useUserStore();
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        if (!requireAuth) {
          // 不需要登录的页面，直接通过
          setIsAuthenticated(true);
          setIsLoading(false);
          return;
        }

        if (!token) {
          // 没有 token，重定向到登录页
          console.log('RouteGuard: 未登录，重定向到登录页');
          const fromParam = encodeURIComponent(location.pathname + location.search);
          navigate(`${redirectTo}?from=${fromParam}`, {
            replace: true,
          });
          return;
        }

        // 有 token，尝试获取用户信息验证有效性
        try {
          await getUser(true);
          setIsAuthenticated(true);
          console.log('RouteGuard: 登录验证成功');
        } catch (error) {
          // token 无效，重定向到登录页
          logout();
          console.log('RouteGuard: 登录验证失败，重定向到登录页', error);
          const fromParam = encodeURIComponent(location.pathname + location.search);
          navigate(`${redirectTo}?from=${fromParam}`, {
            replace: true,
          });
          return;
        }
      } catch (error) {
        console.error('RouteGuard: 验证失败', error);
        const fromParam = encodeURIComponent(location.pathname + location.search);
        navigate(`${redirectTo}?from=${fromParam}`, {
          replace: true,
        });
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [token, requireAuth, redirectTo, navigate, location.pathname, getUser, logout]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen w-screen">
        <SpinLoading color="primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return <>{children}</>;
};

export default RouteGuard;
